<template>
  <BoxCard>
    <template #header>
      <div class="header-container full-width">
        <ChartHeader
          :report-url="baseDetailReportURL"
          title="Job Revenue"
          tooltip-text="Job Revenue = Job Total $ - Sales Tax $"
          @cumulative-change="cumulativeChange"
        />
      </div>
    </template>
    <template #body>
      <div class="relative full-height full-width d-flex">
        <Chart
          v-if="chartData"
          :labels="labels"
          :series="chartData"
          :format-y-axis="formatTicks"
          @point-click="handleClick"
          @point-hover="setCurrentPoint"
        >
          <template #tooltip>
            <ComparisonTooltip
              v-if="currentPoint"
              :point="currentPoint"
              metric="Revenue"
              :format-value="formatDollars"
              format-date
            />
          </template>
        </Chart>
        <img v-else class="skeleton" :src="LineChartSkeletonImg" />
      </div>
    </template>
  </BoxCard>
</template>

<script>
import { mapState } from "vuex";
import {
  formatComparisonSeriesData,
  getComparisonLegend,
  timeSeriesChartClick
} from "@/lib/charts.js";
import { cloneObj } from "@/lib/helpers.js";

import BoxCard from "@/components/BoxCard.vue";
import Chart from "@/components/Chart/Chart.vue";
import ComparisonTooltip from "@/components/Chart/ComparisonTooltip.vue";
import LineChartSkeletonImg from "@/images/line_chart_skeleton.png";
import ChartHeader from "./ChartHeader.vue";

export default {
  components: {
    BoxCard,
    Chart,
    ComparisonTooltip,
    ChartHeader
  },
  data() {
    return {
      chartData: null,
      labels: [],
      currentPoint: null,
      cumulative: true,
      LineChartSkeletonImg
    };
  },
  computed: {
    ...mapState({
      dashboardFilters: "dashboardFilters",
      data: function ({ dashboardData }) {
        return dashboardData.revenue ? dashboardData.revenue.data : [];
      },
      baseDetailReportURL: function ({ dashboardData }) {
        return dashboardData.revenue
          ? dashboardData.revenue.base.detail_report_url
          : "";
      }
    })
  },
  watch: {
    data(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.generateChartData();
      }
    }
  },
  mounted() {
    this.generateChartData();
  },
  beforeUnmount() {
    this.chartData = null;
    this.labels = [];
  },
  methods: {
    cumulativeChange(value) {
      this.cumulative = value;
      this.generateChartData();
    },
    formatTicks(value) {
      return `$${value / 1000}k`;
    },
    formatDollars(value) {
      return this.dollars(value);
    },
    getFormattedData() {
      return [...this.data].map((data) => {
        // Clone point to avoid mutation
        const point = cloneObj(data);
        if (point.base && point.base.cumulative) {
          point.base.cumulative = (point.base.cumulative / 100).toFixed(2);
          point.base.value = (point.base.value / 100).toFixed(2);
        }
        if (point.comparison && point.comparison.cumulative) {
          point.comparison.cumulative = (
            point.comparison.cumulative / 100
          ).toFixed(2);
          point.comparison.value = (point.comparison.value / 100).toFixed(2);
        }
        return point;
      });
    },
    generateChartData() {
      if (this.data.length) {
        const dataDollars = this.getFormattedData();
        const legend = getComparisonLegend();
        const chart = formatComparisonSeriesData(
          dataDollars,
          legend.base,
          legend.comparison,
          this.cumulative ? "cumulative" : "value",
          this.dashboardFilters.period
        );
        if (!chart) return;
        this.chartData = chart.chartData;
        this.labels = chart.labels;
      }
    },
    handleClick(e) {
      return timeSeriesChartClick(this.data, e);
    },
    setCurrentPoint(e) {
      const point = this.data[e.point.index];
      this.currentPoint = {
        base: point.base,
        comparison: point.comparison
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.skeleton {
  max-height: 85%;
  max-width: 100%;
  margin: auto;
  display: block;
}
</style>
